import { useEffect, useState } from 'react';
import type { PlayerLine, TeamLineup } from '@ff/core';
import { fetchLineups, type StoredLineups } from '../lib/api.ts';
import { points } from '../lib/format.ts';

/**
 * Both lineups for one matchup, side by side.
 *
 * Loaded when a card is first opened rather than with the scoreboard: six games
 * would mean six times the payload for a page most people scroll past, and the
 * week's document is the same one for every card — so it's cached here across
 * every matchup on the page.
 *
 * The cache is stamped with the season's sync time and thrown away whole when
 * that moves. Without it a tab left open on a Sunday would show whatever the
 * scores were the first time somebody expanded a card, while the scoreboard
 * above it kept updating — and a 404 from before the week's first sync would
 * never recover.
 */
const cache = new Map<string, StoredLineups | null>();
/** Requests in flight, so open cards share one instead of racing. */
const inFlight = new Map<string, Promise<StoredLineups | null>>();
let cacheStamp = '';

/**
 * The week's lineups, fetched at most once per key per sync.
 *
 * Every card on the page wants the same document, and when a sync invalidates
 * the cache they all want it again at the same moment — so the promise is shared
 * as well as the result.
 */
function load(stamp: string, key: string, fetcher: () => Promise<StoredLineups | null>) {
  if (stamp !== cacheStamp) {
    cache.clear();
    inFlight.clear();
    cacheStamp = stamp;
  }
  if (cache.has(key)) return { cached: true as const, value: cache.get(key) ?? null };

  const existing = inFlight.get(key);
  if (existing) return { cached: false as const, promise: existing };

  const promise = fetcher().then((d) => {
    cache.set(key, d);
    inFlight.delete(key);
    return d;
  });
  // A failure must not leave a permanently pending entry behind.
  promise.catch(() => inFlight.delete(key));
  inFlight.set(key, promise);
  return { cached: false as const, promise };
}

export function Lineups({
  season,
  week,
  syncedAt,
  homeOwnerId,
  awayOwnerId,
  nameOf,
}: {
  season: number;
  week: number;
  /** The season's sync time — what makes a stale lineup cache expire. */
  syncedAt: string;
  homeOwnerId: string;
  awayOwnerId: string;
  nameOf: (id: string) => string;
}) {
  const key = `${season}_${week}`;
  const [data, setData] = useState<StoredLineups | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const result = load(syncedAt, key, () => fetchLineups(season, week));
    if (result.cached) {
      setData(result.value);
      return;
    }
    let mounted = true;
    setError(null);
    result.promise
      .then((d) => mounted && setData(d))
      .catch((e: Error) => mounted && setError(e.message));
    return () => {
      mounted = false;
    };
  }, [key, season, week, syncedAt]);

  if (error) {
    return <p className="py-3 font-mono text-xs text-blood">{error}</p>;
  }
  if (data === undefined) {
    return <p className="py-3 font-mono text-xs text-chalk-dim">loading lineups…</p>;
  }
  if (data === null) {
    return (
      <p className="py-3 font-mono text-xs text-chalk-dim">
        No lineups stored for week {week} yet — they arrive with the next sync.
      </p>
    );
  }

  const home = data.teams.find((t) => t.ownerId === homeOwnerId);
  const away = data.teams.find((t) => t.ownerId === awayOwnerId);
  if (!home || !away) {
    return (
      <p className="py-3 font-mono text-xs text-chalk-dim">
        Couldn't match these teams to the stored lineups.
      </p>
    );
  }

  return (
    <div className="space-y-4 pt-3">
      <p className="font-mono text-[0.6rem] text-chalk-dim">
        dimmed figures are projections, until they play
      </p>
      <div className="grid gap-5 sm:grid-cols-2 sm:gap-8">
        <TeamColumn team={home} name={nameOf(homeOwnerId)} />
        <TeamColumn team={away} name={nameOf(awayOwnerId)} />
      </div>
    </div>
  );
}

function TeamColumn({ team, name }: { team: TeamLineup; name: string }) {
  const [showBench, setShowBench] = useState(false);

  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between border-b border-felt-line pb-1">
        <span className="truncate font-display text-sm font-bold">{name}</span>
        <span className="shrink-0 font-mono text-[0.65rem] text-chalk-dim">
          {team.starters.some((p) => p.hasScore)
            ? `${points(team.startedPoints)} pts`
            : `proj ${points(team.projected)}`}
        </span>
      </div>

      {team.starters.map((p) => (
        <PlayerRow key={p.playerId} player={p} />
      ))}

      <button
        onClick={() => setShowBench((v) => !v)}
        className="w-full pt-2 text-left font-mono text-[0.65rem] uppercase tracking-wider text-chalk-dim hover:text-brass"
      >
        {showBench ? '▾' : '▸'} bench
        {/* "0.0 left on it" before kickoff is noise; the number only means
            something once the bench has actually played. */}
        {team.bench.some((p) => p.hasScore) && ` · ${points(team.benchPoints)} left on it`}
      </button>

      {showBench && (
        <div className="opacity-60">
          {team.bench.map((p) => (
            <PlayerRow key={p.playerId} player={p} />
          ))}
        </div>
      )}
    </div>
  );
}

function PlayerRow({ player }: { player: PlayerLine }) {
  // Before kickoff every score is 0, and a column of zeroes tells you nothing —
  // show what ESPN expects instead, and swap to the real number once it exists.
  // Keyed on whether ESPN has a result at all, because a shut-out defense really
  // did score 0.0 and should say so rather than reverting to its projection.
  const played = player.hasScore;
  // The slot already says the position for every fixed spot; it's only the flex
  // spots where "which RB is this" is a real question. Everywhere else the
  // position would just be the slot again, in a column that costs name width.
  const meta = player.slot === player.position ? player.proTeam : `${player.position} ${player.proTeam}`;

  return (
    <div className="flex items-baseline gap-2 py-0.5 font-mono text-xs">
      <span className="w-9 shrink-0 text-[0.65rem] uppercase tracking-wider text-chalk-dim">
        {player.slot}
      </span>
      <span className="min-w-0 flex-1 truncate font-body text-chalk">
        {player.name}
        {player.injury && (
          <span className="ml-1 text-[0.6rem] uppercase text-blood">
            {player.injury.slice(0, 1)}
          </span>
        )}
        <span className="ml-1.5 text-[0.6rem] uppercase tracking-wide text-chalk-dim">
          {meta}
        </span>
      </span>
      <span
        className={`w-11 shrink-0 text-right tabular ${played ? 'text-chalk' : 'text-chalk-dim'}`}
      >
        {played ? points(player.points) : points(player.projected)}
      </span>
    </div>
  );
}
